import axios, { type InternalAxiosRequestConfig } from 'axios'
import store from '@/store'
import router from '@/router'
import renewExpiringToken from '@/services/auth0/renewExpiringToken'
import debug from '@/helpers/debug'
import { delayMs, readDevApiLatencyMs } from '@/helpers/devApiLatency'
import { GLOBAL_ERROR, MAINTENANCE } from '@/store/mutation-types'

axios.defaults.baseURL = import.meta.env.VITE_SWIFTAID_API_URL

axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    debug('request:', config, 'info')
    if (localStorage.getItem('testEnv') === 'true') {
      config.headers.set(
        'Authorization',
        `Bearer ${localStorage.getItem('token_id')}`
      )
    } else if (import.meta.env.MODE === 'mockapi') {
      // Mirage dev uses storeMockToken, skip Auth0 silent renew (avoids login_required).
      config.headers.set(
        'Authorization',
        // @ts-ignore
        `Bearer ${store.state.auth.idToken}`
      )
    } else {
      void renewExpiringToken().catch((error) => {
        debug('debug: renew token skipped or failed', error)
      })
      config.headers.set(
        'Authorization',
        // @ts-ignore
        `Bearer ${store.state.auth.idToken}`
      )
    }
    return config
  },
  (error) => {
    debug('request error:', error)
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  async (response) => {
    // Mirage adds its own delay in mockapi mode; this is for real v1-dev / prod-like dev.
    if (import.meta.env.MODE !== 'mockapi') {
      const latencyMs = readDevApiLatencyMs()
      if (latencyMs > 0) {
        await delayMs(latencyMs)
      }
    }

    debug('response:', response, 'info')
    store.commit(GLOBAL_ERROR, null)
    return response
  },
  async (error) => {
    debug('response error:', error, 'info')

    const requestPath = (
      error.response?.config?.url ??
      error.config?.url ??
      ''
    ).split('?')[0]

    // Don't show global error for conflict errors specific to /me/accounts
    if (
      error.response &&
      error.response.status === 409 &&
      requestPath.includes('/me/accounts')
    ) {
      throw error
    }

    // Adding or editing a donation reports its own failure in the form. A 400
    // here would otherwise paint the page banner as well as that message.
    const method = error.config?.method
    const isDonationWrite =
      (method === 'post' &&
        (requestPath.endsWith('/me/donations/manual') ||
          requestPath.endsWith('/me/donations/recurring'))) ||
      (method === 'put' && requestPath.includes('/me/donations/manual/'))
    if (error.response?.status === 400 && isDonationWrite) {
      throw error
    }

    // The user endpoint (GET/POST /me) drives the login flow, not a global banner:
    // a 404 (no donor yet) and a 409 (donor scheduled for deletion) are handled by
    // fetchUser + the Whoops screen. Suppress the global error for both.
    if (
      error.response &&
      requestPath.endsWith('/me') &&
      (error.response.status === 404 || error.response.status === 409)
    ) {
      throw error
    }

    if (error.response) {
      // NOTE: handle errors globally with general error msg
      // create instances for urls outside streeva api.
      switch (error.response.status) {
        case 400:
          store.commit(GLOBAL_ERROR, 'AxiosError.BadRequest')
          break
        case 401:
          store.commit(GLOBAL_ERROR, 'AxiosError.Unauthorised')
          break
        case 403:
          store.commit(GLOBAL_ERROR, 'AxiosError.Forbidden')
          break
        case 404:
          store.commit(GLOBAL_ERROR, 'AxiosError.NotFound')
          break
        case 409:
          store.commit(GLOBAL_ERROR, 'AxiosError.Default')
          break
        case 500:
          store.commit(GLOBAL_ERROR, 'AxiosError.Server')
          break
        case 503:
          store.commit(GLOBAL_ERROR, 'AxiosError.ServiceUnavailable')
          store.commit(MAINTENANCE, true)
          router.replace({ name: 'Maintenance' }).catch(() => {})
          break
        default:
          store.commit(GLOBAL_ERROR, 'AxiosError.Default')
      }
    }
    throw error
  }
)
